import React, { useState, useContext, useEffect } from 'react';
import { Box, Grid, Card, CardContent, Button, TextField, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Alert } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import moment from 'moment';
import ImageUploader from './ImageUploader';
import { getAuthHeaders, getAuthHeader, getImageDisplayUrl, getUploadApiBaseUrl } from '../../utils/api';

const UpdateServiceLog = () => {
  const { log_id } = useParams(); // Hämta loggpostens ID från URL:en
  const [userId, setUserId] = useState('');
  const { currentUser } = useAuth();
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [boatNameModel, setBoatNameModel] = useState('');
  const [engineType, setEngineType] = useState('');
  const [servicePersonOrCompany, setServicePersonOrCompany] = useState('');
  const [serviceType, setServiceType] = useState('');
  const [engineService, setEngineService] = useState('');
  const [sailAndRig, setSailAndRig] = useState('');
  const [electronicsAndNavigation, setElectronicsAndNavigation] = useState('');
  const [hullAndDeck, setHullAndDeck] = useState('');
  const [rudderPropellerAndDrive, setRudderPropellerAndDrive] = useState('');
  const [fuelSystem, setFuelSystem] = useState('');
  const [batteryAndElectricalSystem, setBatteryAndElectricalSystem] = useState('');
  const [sparePartsAndReplacements, setSparePartsAndReplacements] = useState('');
  const [recommendedActions, setRecommendedActions] = useState('');
  const [costAndTime, setCostAndTime] = useState('');
  const [comments, setComments] = useState('');
  const [images, setImages] = useState([]); 
  const [uploadedImages, setUploadedImages] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showLoginMessage, setShowLoginMessage] = useState(false);
  const [open, setOpen] = useState(false); 
  const [closeWithoutSaveOpen, setCloseWithoutSaveOpen] = useState(false);
  const navigate = useNavigate();
  const [imagesToRemove, setImagesToRemove] = useState([]);

  useEffect(() => {
    if (currentUser === null) {
      navigate('/info');
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    // Skapa en ny abort controller
    const abortController = new AbortController();

    // Hämta loggpostens data när komponenten laddas
    fetch(`${process.env.REACT_APP_SERVER_URL}/api/servicelog/${log_id}`, { 
      signal: abortController.signal,
      headers: getAuthHeaders()
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        // Sätt state med data från servern, använd tom sträng som fallback för undefined/null
        setUserId(data.userId || '');
        setTitle(data.title || '');
        setDate(data.date ? moment(data.date).format('YYYY-MM-DDTHH:mm') : '');
        setBoatNameModel(data.boatNameModel || '');
        setEngineType(data.engineType || '');
        setServicePersonOrCompany(data.servicePersonOrCompany || '');
        setServiceType(data.serviceType || '');
        setEngineService(data.engineService || '');
        setSailAndRig(data.sailAndRig || '');
        setElectronicsAndNavigation(data.electronicsAndNavigation || '');
        setHullAndDeck(data.hullAndDeck || '');
        setRudderPropellerAndDrive(data.rudderPropellerAndDrive || '');
        setFuelSystem(data.fuelSystem || '');
        setBatteryAndElectricalSystem(data.batteryAndElectricalSystem || '');
        setSparePartsAndReplacements(data.sparePartsAndReplacements || '');
        setRecommendedActions(data.recommendedActions || '');
        setCostAndTime(data.costAndTime || '');
        setComments(data.comments || '');
        // Hantera bilder - kan vara array, sträng, null eller undefined
        let imagesArray = data.images || [];
        if (typeof imagesArray === 'string') {
          try {
            imagesArray = JSON.parse(imagesArray);
          } catch (e) {
            imagesArray = [];
          }
        }
        if (!Array.isArray(imagesArray)) {
          imagesArray = [];
        }
        setImages(imagesArray);
      })
      .catch(error => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted');
        } else {
          console.error('Error:', error);
        }
      });

    // Avbryt fetch-anropet när komponenten avmonteras
    return () => {
      abortController.abort();
    };
  }, [log_id]); // Kör useEffect när `log_id` ändras

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleCloseWithoutSaveRequest = () => {
    setCloseWithoutSaveOpen(true);
  };

  const handleCloseWithoutSaveDialogClose = () => {
    setCloseWithoutSaveOpen(false);
  };

  const handleCloseWithoutSaveConfirm = () => {
    setCloseWithoutSaveOpen(false);
    navigate(`/showservicelog/${log_id}`);
  };

  const handleDelete = () => {
    fetch(`${process.env.REACT_APP_SERVER_URL}/api/servicelog/${log_id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    })
      .then(response => response.text())
      .then(data => {
        console.log(data);
        navigate('/servicelogs'); // Navigera till startsidan efter en lyckad radering
      })
      .catch((error) => {
        console.error('Error:', error);
      });
    handleClose();
  };

  const handleRemoveExistingImage = (event, indexToRemove) => {
    event.preventDefault();
    const imageToRemove = images[indexToRemove];
    setImagesToRemove(prev => [...prev, imageToRemove]);
    setImages(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setShowLoginMessage(false);
    const formData = new FormData();
    selectedFiles.forEach((file, index) => {
      formData.append(`file${index}`, file);
    });
    formData.append('accountId', userId);
    formData.append('type', 'servicelog');

    let newUploadedUrls = [];
    if (selectedFiles.length > 0) {
      const uploadResponse = await fetch(`${getUploadApiBaseUrl()}/api/uploads`, {
        method: 'POST',
        headers: getAuthHeader() ? { Authorization: getAuthHeader() } : {},
        body: formData
      });
      if (!uploadResponse.ok) {
        const errData = await uploadResponse.json().catch(() => ({}));
        alert(errData.message || 'Kunde inte ladda upp bilder.');
        return;
      }
      newUploadedUrls = await uploadResponse.json();
    }

    const updatedImages = [...images, ...newUploadedUrls];

    // Skapa ett objekt med uppdaterade data att skicka till servern
    const serviceLogData = {
      userId,
      title,
      date,
      boatNameModel,
      engineType,
      servicePersonOrCompany,
      serviceType,
      engineService,
      sailAndRig,
      electronicsAndNavigation,
      hullAndDeck,
      rudderPropellerAndDrive,
      fuelSystem,
      batteryAndElectricalSystem,
      sparePartsAndReplacements,
      recommendedActions,
      costAndTime,
      comments,
      images: updatedImages // Använd updatedImages här
    };

    imagesToRemove.forEach(urlOrPath => {
      fetch(`${getUploadApiBaseUrl()}/api/deleteimage`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        body: JSON.stringify({ url: urlOrPath })
      })
        .then(response => response.text())
        .then(data => console.log(data))
        .catch((error) => console.error('Error:', error));
    });

    // Skicka till servern (servern avgör behörighet, t.ex. inbjuden redaktör)
    fetch(`${process.env.REACT_APP_SERVER_URL}/api/servicelog/${log_id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(serviceLogData)
    })
      .then(response => {
        if (!response.ok) {
          if (response.status === 403) setShowLoginMessage(true);
          return;
        }
        return response.text();
      })
      .then(data => {
        if (data === undefined) return;
        console.log(data);
        navigate(`/showservicelog/${log_id}`);
      })
      .catch((error) => {
        console.error('Error:', error);
      });
  };

  return (
    <Box sx={{ maxWidth: '1200px', margin: '0 auto', padding: { xs: 2, md: 4 } }}>
      <Card>
        <CardContent>
          {showLoginMessage && (
            <Alert severity="warning" onClose={() => setShowLoginMessage(false)}>
              Du har inte behörighet att uppdatera denna logg.
            </Alert>
          )}
          <form onSubmit={handleSubmit} style={{ marginLeft: '16px', marginRight: '16px' }}>
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Rubrik"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Datum"
                  type="datetime-local"
                  value={date}
                  onChange={(event) => setDate(event.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Båtens namn/modell"
                  value={boatNameModel}
                  onChange={(event) => setBoatNameModel(event.target.value)}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Motor/typ"
                  value={engineType}
                  onChange={(event) => setEngineType(event.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Serviceperson eller företag"
                  value={servicePersonOrCompany}
                  onChange={(event) => setServicePersonOrCompany(event.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Typ av service"
                  value={serviceType}
                  onChange={(event) => setServiceType(event.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Motorservice"
                  value={engineService}
                  onChange={(event) => setEngineService(event.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  InputProps={{
                    style: { resize: 'vertical' }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Segel och rigg"
                  value={sailAndRig}
                  onChange={(event) => setSailAndRig(event.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  InputProps={{
                    style: { resize: 'vertical' }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Elektronik och navigation"
                  value={electronicsAndNavigation}
                  onChange={(event) => setElectronicsAndNavigation(event.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  InputProps={{
                    style: { resize: 'vertical' }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Skrov och däck"
                  value={hullAndDeck}
                  onChange={(event) => setHullAndDeck(event.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  InputProps={{
                    style: { resize: 'vertical' }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Roder, propeller och drivning"
                  value={rudderPropellerAndDrive}
                  onChange={(event) => setRudderPropellerAndDrive(event.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  InputProps={{
                    style: { resize: 'vertical' }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Bränslesystem"
                  value={fuelSystem}
                  onChange={(event) => setFuelSystem(event.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  InputProps={{
                    style: { resize: 'vertical' }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Batteri och elsystem"
                  value={batteryAndElectricalSystem}
                  onChange={(event) => setBatteryAndElectricalSystem(event.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  InputProps={{
                    style: { resize: 'vertical' }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Reservdelar och byten"
                  value={sparePartsAndReplacements}
                  onChange={(event) => setSparePartsAndReplacements(event.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  InputProps={{
                    style: { resize: 'vertical' }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Rekommenderade åtgärder"
                  value={recommendedActions}
                  onChange={(event) => setRecommendedActions(event.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  InputProps={{
                    style: { resize: 'vertical' }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Kostnad och tid"
                  value={costAndTime}
                  onChange={(event) => setCostAndTime(event.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  InputProps={{
                    style: { resize: 'vertical' }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Kommentarer"
                  value={comments}
                  onChange={(event) => setComments(event.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  InputProps={{
                    style: { resize: 'vertical' }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <ImageUploader onUploadSuccess={(files) => setSelectedFiles(prev => [...prev, ...files])} />
                    <Box display="flex" flexWrap="wrap" justifyContent="flex-start">
                      {images.map((imageUrl, index) => (
                        <Box padding={2} key={index} width={300}>
                          <Card sx={{ padding: 1, position: 'relative' }}>
                            <CardContent>
                              <button onClick={(event) => handleRemoveExistingImage(event, index)}
                                style={{
                                  position: 'absolute',
                                  top: '0',
                                  right: '0',
                                  background: 'red',
                                  color: '#fff',
                                  border: 'none',
                                  cursor: 'pointer'
                                }}
                              >X</button>

                              <img src={getImageDisplayUrl(imageUrl)} alt={`Image ${index}`} style={{ width: '100%' }} />
                            </CardContent>
                          </Card>
                        </Box>
                      ))}
                      {uploadedImages.map((imageUrl, index) => (
                        <Box padding={2} key={index} width={300}>
                          <Card sx={{ padding: 1 }}>
                            <CardContent>
                              <img src={getImageDisplayUrl(imageUrl)} alt="Uppladdad bild" style={{ width: '100%' }} />
                            </CardContent>
                          </Card>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Box paddingTop={4} paddingRight={2} paddingBottom={4} paddingLeft={0}>
                <Button variant="contained" color="primary" type="submit">Uppdatera</Button>
                <Button 
                    variant="contained" 
                    style={{ marginLeft: '10px', backgroundColor: '#E3F2FD', color: '#1565C0' }}
                    type="button"
                    onClick={handleCloseWithoutSaveRequest}
                    sx={{
                        '&:hover': {
                            backgroundColor: '#BBDEFB',
                        },
                    }}
                >
                    Tillbaka
                </Button>
              </Box>
            </Grid>
          </form>
        </CardContent>
      </Card >
      <Box paddingTop={4} paddingRight={2} paddingBottom={4} paddingLeft={0}>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" color="warning" onClick={handleClickOpen}>Radera servicelogg</Button>
          <Dialog
            open={open}
            onClose={handleClose}
          >
            <DialogTitle>Är du säker?</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Är du säker på att du vill radera denna servicelogg? Denna åtgärd kan inte ångras.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} color="primary">Avbryt</Button>
              <Button 
                  onClick={handleDelete}
                  sx={{
                      backgroundColor: '#E3F2FD',
                      color: '#1565C0',
                      '&:hover': {
                          backgroundColor: '#BBDEFB',
                      },
                  }}
              >
                  Radera
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      </Box>

      <Dialog
        open={closeWithoutSaveOpen}
        onClose={handleCloseWithoutSaveDialogClose}
      >
        <DialogTitle>Är du säker på att du vill stänga utan att spara?</DialogTitle>
        <DialogActions>
          <Button onClick={handleCloseWithoutSaveDialogClose} color="primary">Avbryt</Button>
          <Button
            onClick={handleCloseWithoutSaveConfirm}
            sx={{
              backgroundColor: '#E3F2FD',
              color: '#1565C0',
              '&:hover': {
                backgroundColor: '#BBDEFB',
              },
            }}
          >
            Stäng utan att spara
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

export default UpdateServiceLog;